import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { MoreOrganizerEventCard } from './MoreOrganizerEventCard';
import styles from './MoreOrganizerEvents.module.scss';
import { Divider } from '../_shared/Divider';
import { useMoreOrganizerEventsSectionModel } from './hooks';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { OrganizerEvent } from './types';
import { BasicInfo } from '@/types';
import { AffiliateCodes, DOM_IDS } from '../_shared/constants';
import { useLocalizedCategory } from '@/hooks';

export const DEFAULT_MAX_EVENTS = 4;

export interface MoreOrganizerEventsProps {
  eventId: string;
  isBodyRendered?: boolean;
  eventBasicInfo: BasicInfo;
  category?: string;
}

const buildOrganizerEventList = (eventsToShow: OrganizerEvent[], affiliateCode?: AffiliateCodes) => {
  return eventsToShow.map((event, index) => {
    const isLastItem = index === eventsToShow.length - 1;
    const eventKey = event.id || `organizer-event-${index}`;

    // Add margin bottom when event is not online and has no venue
    const shouldAddExtraSpace = event.isOnlineEvent === false && !event.venue;

    return (
      <React.Fragment key={eventKey}>
        <div className={shouldAddExtraSpace ? styles.undeterminedVenue : undefined}>
          <MoreOrganizerEventCard organizerEvent={event} affiliateCode={affiliateCode} />
        </div>
        {!isLastItem && <Divider />}
      </React.Fragment>
    );
  });
};

export const MoreOrganizerEvents: React.FC<MoreOrganizerEventsProps> = ({
  eventId,
  isBodyRendered,
  eventBasicInfo,
  category,
}) => {
  const { formatMessage } = useIntl();
  const { affiliateCode, containerRef, eventsToShow, hasEvents, isLoading } = useMoreOrganizerEventsSectionModel({
    eventBasicInfo,
    eventId,
    isBodyRendered,
  });

  const organizerEventsList = useMemo(
    () => buildOrganizerEventList(eventsToShow, affiliateCode),
    [eventsToShow, affiliateCode],
  );

  const title = useMemo(() => {
    if (eventBasicInfo.organizer?.name) {
      return formatMessage(
        { id: 'More events from {organizerName}' },
        { organizerName: eventBasicInfo.organizer.name },
      );
    }

    return formatMessage({ id: 'More events from this organizer' });
  }, [eventBasicInfo.organizer?.name, formatMessage]);

  const localizedCategory = useLocalizedCategory(category);

  const subtitle = useMemo(() => {
    if (eventBasicInfo.organizer?.name && localizedCategory) {
      return formatMessage(
        {
          id: 'Discover more events from {organizerName}, from {mainCategory} to other experiences you might love.',
        },
        {
          organizerName: eventBasicInfo.organizer.name,
          mainCategory: localizedCategory,
        },
      );
    }

    return formatMessage({ id: 'Follow organizers to get events picked for you' });
  }, [eventBasicInfo.organizer?.name, formatMessage, localizedCategory]);

  const shouldShowSkeleton = isLoading;
  const shouldShowEmpty = !isLoading && !hasEvents;
  const shouldShowEvents = !isLoading && hasEvents;

  const containerStyle = useMemo(
    () => ({
      ...(shouldShowEmpty && { minHeight: '1px' }),
    }),
    [shouldShowEmpty],
  );

  const containerProps = shouldShowEvents ? { 'data-testid': 'more-organizer-events' } : {};

  // Only render if we have an organizer
  if (!eventBasicInfo.organizer?.id) {
    return null;
  }

  return (
    <div ref={containerRef} style={containerStyle} {...containerProps} id={DOM_IDS.MORE_ORGANIZER_EVENTS}>
      <div className={!shouldShowEvents ? styles.hiddenSeoCopy : ''} data-testid="more-organizer-events-copy">
        <h2 className={styles.title}>{title}</h2>
        <h3 className={styles.subtitle}>{subtitle}</h3>
      </div>
      {shouldShowSkeleton ? <LoadingSkeleton height={550} /> : null}
      {shouldShowEvents ? (
        <section className={styles.moreOrganizerEventsContainer}>
          {organizerEventsList}
          <div className={styles.dividerContainer}>
            <Divider />
          </div>
        </section>
      ) : null}
    </div>
  );
};
